import type { ServiceAction } from "@rakazo/contracts";
import { EMPTY_SERVICE_WORKSPACE } from "@rakazo/contracts";
import { writeSaved } from "@rakazo/core/query";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useRef } from "react";
import { spaceQueries, useQueryUserId } from "../../lib/query";
import { createSpaceRpc } from "../../lib/rpc";

/** All keys include the signed-in user and space; no fixture data reaches this hook. */
export function useCustomerService(spaceId: string | null) {
  const queries = spaceQueries(useQueryUserId(), spaceId);
  const client = useMemo(() => createSpaceRpc(spaceId), [spaceId]);
  const cache = useQueryClient();
  const options = queries.customerService.workspace.queryOptions();
  const workspace = useQuery({
    ...options,
    refetchInterval: 10_000,
    refetchIntervalInBackground: false,
  });
  const requests = useRef(new Map<string, string>());
  const mutation = useMutation({
    mutationFn: async (action: ServiceAction) => {
      const key = JSON.stringify(action);
      const requestId = requests.current.get(key) ?? crypto.randomUUID();
      requests.current.set(key, requestId);
      const snapshot = await client.customerService.act({ requestId, action });
      requests.current.delete(key);
      return snapshot;
    },
    retry: false,
    onSuccess: (snapshot) => writeSaved(cache, options.queryKey, () => snapshot),
  });
  return {
    snapshot: workspace.data ?? EMPTY_SERVICE_WORKSPACE,
    loading: workspace.isPending,
    error: workspace.error,
    refreshing: workspace.isFetching,
    refresh: () => workspace.refetch(),
    act: mutation.mutateAsync,
    pending: mutation.isPending,
  };
}
